import type { Upload, UploadWidget } from '@oneapp/shared';
import { type DragEvent, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/icons';
import { Sheet } from '../components/Sheet';
import { Button, Callout } from '../components/ui';
import { ApiError } from '../lib/api';
import { fileLabel, importsApi } from './api';

/**
 * U1 · Upload your sales file: choose a file (or drop one on a computer), and it goes straight to
 * storage through a short-lived link; the server checks it (what it really is, a malware scan) before
 * the chat reads it. The template's columns show what a file needs. Nothing is read here.
 */

type Stage = { kind: 'idle' } | { kind: 'sending'; file: File } | { kind: 'checking'; file: File };

/** Why a file can't go up, in words the person can act on. */
type Refusal =
  | 'type'
  | 'xls'
  | 'size'
  | 'failed'
  | 'wrong_type'
  | 'old_excel'
  | 'infected'
  | 'scan_failed'
  | 'size_changed'
  | 'missing';

/** The server's reasons for refusing a file, as what the sheet says. */
const REFUSALS: Record<string, Refusal> = {
  wrong_type: 'wrong_type',
  old_excel: 'old_excel',
  infected: 'infected',
  scan_failed: 'scan_failed',
  size: 'size_changed',
  missing: 'missing',
};

const ENDINGS = ['.csv', '.xlsx'];

export function UploadSheet({
  widget,
  onClose,
  onUploaded,
}: {
  widget: UploadWidget;
  onClose: () => void;
  /** The file is up and passed the checks: the chat takes it from here. */
  onUploaded: (upload: Upload) => void;
}) {
  const { t } = useTranslation();
  const notice = widget.purpose === 'gst_notice';
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [dragging, setDragging] = useState(false);
  const maxBytes = (widget.maxSizeMb ?? 5) * 1024 * 1024;
  const busy = stage.kind !== 'idle';

  const send = async (file: File) => {
    const name = file.name.toLowerCase();
    if (name.endsWith('.xls')) return setRefusal('xls');
    if (!(notice ? ['.pdf', '.jpg', '.jpeg', '.png', '.webp'] : ENDINGS).some((ending) => name.endsWith(ending)))
      return setRefusal('type');
    if (file.size > maxBytes) return setRefusal('size');
    if (file.size === 0) return setRefusal('failed');
    setRefusal(null);
    setStage({ kind: 'sending', file });
    try {
      const ticket = await importsApi.createUpload({
        purpose: notice ? 'gst_notice' : 'sales_file',
        fileName: file.name,
        contentType: file.type,
        size: file.size,
      });
      await importsApi.put(ticket, file);
      setStage({ kind: 'checking', file });
      const { upload } = await importsApi.complete(ticket.upload.id);
      if (upload.status === 'rejected') {
        setRefusal(REFUSALS[upload.rejectReason ?? ''] ?? 'failed');
        setStage({ kind: 'idle' });
        return;
      }
      onUploaded(upload);
    } catch (error) {
      const code = error instanceof ApiError ? error.problem?.code : undefined;
      setRefusal(code === 'FILE_TOO_BIG' ? 'size' : code === 'FILE_TYPE_NOT_ALLOWED' ? 'type' : 'failed');
      setStage({ kind: 'idle' });
    } finally {
      if (input.current) input.current.value = '';
    }
  };

  const drop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    if (file && !busy) void send(file);
  };

  const templates = widget.templates?.length ? widget.templates : widget.template ? [widget.template] : [];
  const [mainTemplate, ...otherTemplates] = templates;

  return (
    <Sheet
      open
      onClose={busy ? () => {} : onClose}
      label={widget.secureLabel ?? t('chat.widget.secure')}
      footer={
        <div className="sheet__buttons sheet__buttons--stack">
          {mainTemplate ? (
            <a className="btn btn--secondary" href={mainTemplate.url} download>
              <Icon name="download" size={18} />
              {mainTemplate.label}
            </a>
          ) : null}
          <Button icon="upload" busy={busy} onClick={() => input.current?.click()}>
            {widget.cta}
          </Button>
        </div>
      }
    >
      <h2 className="sheet__title">{widget.title}</h2>
      <label
        htmlFor={inputId}
        className={`dropzone${dragging ? ' dropzone--over' : ''}${busy ? ' dropzone--busy' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={drop}
      >
        <span className="icon-tile icon-tile--large">
          <Icon name={busy ? 'file-text' : 'upload'} size={25} />
        </span>
        {stage.kind === 'idle' ? (
          <>
            <span className="dropzone__title">{t('imports.upload.choose')}</span>
            <span className="dropzone__hint">
              {t(notice ? 'imports.notice.limits' : 'imports.upload.limits', { size: widget.maxSizeMb ?? 5 })}
            </span>
          </>
        ) : (
          <>
            <span className="dropzone__title">{stage.file.name}</span>
            <span className="dropzone__hint" role="status">
              {fileLabel(stage.file.name, stage.file.size)} ·{' '}
              {stage.kind === 'sending' ? t('imports.upload.sending') : t('imports.upload.checking')}
            </span>
            <span className="progress" aria-hidden="true">
              <i />
            </span>
          </>
        )}
        <input
          ref={input}
          id={inputId}
          type="file"
          className="visually-hidden"
          accept={(notice
            ? widget.accept
            : [...widget.accept, 'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
          ).join(',')}
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void send(file);
          }}
        />
      </label>
      {refusal ? (
        <Callout tone="error" icon="alert-triangle" role="alert">
          {t(
            notice && ['type', 'wrong_type', 'xls', 'old_excel'].includes(refusal)
              ? 'imports.notice.type'
              : notice && ['size', 'infected'].includes(refusal)
                ? `imports.notice.${refusal}`
                : `imports.upload.refused.${refusal}`,
            { size: widget.maxSizeMb ?? 5 },
          )}
        </Callout>
      ) : null}

      {widget.columns?.length ? (
        <div className="upload-template">
          <span className="upload-template__title">{t('imports.upload.template')}</span>
          <ul className="upload-template__columns" aria-label={t('imports.upload.columns')}>
            {widget.columns.map((column) => (
              <li key={column} className="chip chip--mono chip--static">
                {column}
              </li>
            ))}
          </ul>
          {widget.note ? <p className="sheet__text muted">{widget.note}</p> : null}
          {otherTemplates.map((link) => (
            <a key={link.url} className="linkbtn" href={link.url} download>
              {link.label}
            </a>
          ))}
        </div>
      ) : null}
    </Sheet>
  );
}
